import { Injectable } from '@angular/core';

import { AnyCustomPropertyValue } from '@reach/interfaces/custom-property/custom-property-value.interface';
import {
	NodeCreationSource,
	NodeCreationSourceBody,
} from '@reach/interfaces/nodes/node-creation-source.interface';
import { NoteInterface } from '@reach/interfaces/notes/note.interface';
import { NoteCreateBody, NoteUpdateBody } from '@reach/interfaces/notes/notes.requests';
import {
	ConvertNoteAttachmentToFileResponse,
	CreateNoteResponse,
	DeleteAttachmentFromNoteResponse,
	UpdateNoteResponse,
} from '@reach/interfaces/notes/notes.responses';
import { GetElementType } from '@reach/interfaces/typescript/get-element-type';

import { BaseApiService } from '~app-client/api/_internal';
import { ReachNodeId } from '~app-client/core/types';
import { WorkspaceId } from '~app-client/workspaces/types';
import { isPersistedWorkspaceId } from '~app-client/workspaces/utils';

@Injectable({ providedIn: 'root' })
export class NotesApiService extends BaseApiService {
	protected commonPath = ['notes'];

	public async create(
		workspaceIds: WorkspaceId[],
		name: string,
		source?: NodeCreationSource
	): Promise<CreateNoteResponse> {
		const note: NoteCreateBody = { name };
		const sourceBody: NodeCreationSourceBody = { source };
		return await this.http.post<CreateNoteResponse>(this.url(), {
			...note,
			...sourceBody,
			workspaceIds: workspaceIds.filter((id) => isPersistedWorkspaceId(id)),
		});
	}

	public async edit(noteId: ReachNodeId, data: NoteUpdateBody): Promise<UpdateNoteResponse> {
		const body = data;
		return await this.http.patch<UpdateNoteResponse>(this.url(noteId), body);
	}

	public async updateCustomProperties(
		noteId: ReachNodeId,
		customProperties: AnyCustomPropertyValue[],
		workspaceId: ReachNodeId
	): Promise<AnyCustomPropertyValue[]> {
		const body = {
			customProperties,
			fatherWorkspaceId: workspaceId,
		};
		return await this.http
			.patch<NoteInterface>(this.url(noteId), body)
			.then((node) => node.customProperties || []);
	}

	public async createNoteAttachment(
		noteId: ReachNodeId,
		file: File
	): Promise<GetElementType<NoteInterface['attachments']>> {
		const formData = new FormData();
		formData.append('file', file);
		return await this.http.post<GetElementType<NoteInterface['attachments']>>(
			this.url(`${noteId}/attachments`),
			formData
		);
	}

	public async deleteNoteAttachment(
		noteId: ReachNodeId,
		attachmentId: string
	): Promise<DeleteAttachmentFromNoteResponse> {
		return await this.http.delete<DeleteAttachmentFromNoteResponse>(
			this.url(`${noteId}/attachments/${attachmentId}`)
		);
	}

	public async attachmentToNode(
		noteId: ReachNodeId,
		attachmentId: string,
		name: string,
		workspaceIds: ReachNodeId[]
	): Promise<ConvertNoteAttachmentToFileResponse> {
		const body = {
			name,
			workspaceIds,
		};
		return await this.http.post<ConvertNoteAttachmentToFileResponse>(
			this.url(`${noteId}/attachments/${attachmentId}/to-node`),
			body
		);
	}
}
